import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import {
  deleteVariableHistoryApi,
  DpVariableHistory,
  listVariableHistoryApi,
  saveVariableHistoryApi,
} from "../api/payrollApi";
import { formatCurrency } from "../utils/format";

const emptyForm = { month: "", workday: "", restDay: "", other: "", notes: "" };

const hours = (value: string) => Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
const monthLabel = (row: Pick<DpVariableHistory, "year" | "month">) => `${String(row.month).padStart(2, "0")}/${row.year}`;

type Props = { token: string; contractId: string; readOnly: boolean };

/**
 * Card "Médias anteriores ao sistema" da aba Contrato (ADR 0034): as variáveis dos meses
 * antes da implantação, lidas do holerite antigo. Entram nas médias de férias, 13º,
 * rescisão e afastamento nos meses sem folha mensal fechada no sistema.
 */
export default function VariableHistoryCard({ token, contractId, readOnly }: Props) {
  const [rows, setRows] = useState<DpVariableHistory[]>([]);
  const [modal, setModal] = useState<{ row?: DpVariableHistory } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<DpVariableHistory | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await listVariableHistoryApi(token, contractId));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar o histórico de variáveis.");
    }
  }, [token, contractId]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (row?: DpVariableHistory) => {
    setForm(
      row
        ? {
            month: `${row.year}-${String(row.month).padStart(2, "0")}`,
            workday: String(Number(row.overtimeWorkdayHours)),
            restDay: String(Number(row.overtimeRestDayHours)),
            other: String(Number(row.otherAmount)),
            notes: row.notes ?? "",
          }
        : emptyForm
    );
    setModal({ row });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.month) return toast.error("Informe o mês.");
    const [year, month] = form.month.split("-").map(Number);
    setSaving(true);
    try {
      await saveVariableHistoryApi(token, contractId, year, month, {
        overtimeWorkdayHours: Number(form.workday || 0),
        overtimeRestDayHours: Number(form.restDay || 0),
        otherAmount: Number(form.other || 0),
        notes: form.notes.trim() || null,
      });
      toast.success("Mês salvo. Vale nos próximos cálculos de férias, 13º, rescisão e afastamento.");
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o mês do histórico.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await deleteVariableHistoryApi(token, contractId, toDelete.year, toDelete.month);
      toast.success("Mês excluído do histórico.");
      setToDelete(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir o mês do histórico.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpSection" style={{ marginBottom: 16 }}>
      <div className="dpHeader" style={{ marginBottom: 12 }}>
        <h3 className="dpSection__title" style={{ margin: 0 }}>
          Médias anteriores ao sistema
        </h3>
        {!readOnly ? (
          <button className="dpBtn dpBtn--secondary" onClick={() => openModal()}>
            Novo mês
          </button>
        ) : null}
      </div>
      <p className="dpFormHint">
        Horas extras e demais variáveis dos meses antes da implantação, do holerite antigo. Entram nas médias de
        férias, 13º, rescisão e afastamento por doença nos meses sem folha fechada no sistema. As horas extras são
        valoradas pela hora de hoje (média física, como o contador).
      </p>

      {rows.length === 0 ? (
        <p className="dpFormHint">Nenhum mês informado.</p>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Mês</th>
                <th>HE 50% (h)</th>
                <th>HE domingo/feriado (h)</th>
                <th>Outras variáveis</th>
                {!readOnly ? <th style={{ width: 160 }}>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {monthLabel(row)}
                    {row.notes ? <div className="dpFormHint">{row.notes}</div> : null}
                  </td>
                  <td>{hours(row.overtimeWorkdayHours)}</td>
                  <td>{hours(row.overtimeRestDayHours)}</td>
                  <td>{formatCurrency(row.otherAmount)}</td>
                  {!readOnly ? (
                    <td>
                      <div className="dpTable__actions">
                        <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openModal(row)}>
                          Editar
                        </button>
                        <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setToDelete(row)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.row ? `Editar ${monthLabel(modal.row)}` : "Novo mês do histórico"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="vhMonth">Mês *</label>
                  <input
                    id="vhMonth"
                    type="month"
                    className="dpInput"
                    value={form.month}
                    onChange={(e) => setForm({ ...form, month: e.target.value })}
                    disabled={saving || !!modal.row}
                  />
                </div>
                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="vhWorkday">HE em dia de trabalho (h)</label>
                    <input
                      id="vhWorkday"
                      type="number"
                      step="0.0001"
                      min="0"
                      className="dpInput"
                      value={form.workday}
                      onChange={(e) => setForm({ ...form, workday: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="vhRest">HE em domingo/feriado (h)</label>
                    <input
                      id="vhRest"
                      type="number"
                      step="0.0001"
                      min="0"
                      className="dpInput"
                      value={form.restDay}
                      onChange={(e) => setForm({ ...form, restDay: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="vhOther">Outras variáveis (R$)</label>
                    <input
                      id="vhOther"
                      type="number"
                      step="0.01"
                      min="0"
                      className="dpInput"
                      value={form.other}
                      onChange={(e) => setForm({ ...form, other: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                </div>
                <span className="dpFormHint">
                  Outras variáveis: adicional noturno, DSR sobre horas extras e noturno. Mês trabalhado sem variáveis
                  também conta na média: grave com zero.
                </span>
                <div className="dpFormGroup">
                  <label htmlFor="vhNotes">Observação</label>
                  <input
                    id="vhNotes"
                    className="dpInput"
                    maxLength={500}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    disabled={saving}
                  />
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {saving ? "Salvando…" : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={Boolean(toDelete)}
        title="Excluir o mês do histórico?"
        message={toDelete ? `As médias deixam de contar ${monthLabel(toDelete)}.` : ""}
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
